import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface InvoiceRow {
  id: string;
  property_id: string;
  owner_user_id: string;
  payee_type: string;
  payee_id: string;
  status: string;
  period_start: string;
  period_end: string;
  total_amount_cents: number;
  subtotal_amount_cents?: number;
  processing_fee_amount_cents?: number;
  amount_to_charge_cents?: number;
  charged_amount_cents?: number | null;
  transferred_amount_cents?: number | null;
  created_at: string;
  updated_at: string;
  paid_at?: string | null;
  stripe_payment_intent_id?: string | null;
}

export interface InvoiceItemRow {
  id: string;
  invoice_id: string;
  item_type: "cleaning" | "inventory_reimbursement" | "manual" | "cleaner_purchase";
  reference_id: string | null;
  description: string | null;
  quantity: number;
  unit_amount_cents: number;
  amount_cents: number;
  meta: { original_amount_cents?: number; [k: string]: unknown } | null;
  created_at: string;
}

/** List invoices for a property, most recent first. */
export function useInvoicesByProperty(propertyId: string | undefined) {
  return useQuery({
    queryKey: ["invoices-by-property", propertyId],
    queryFn: async () => {
      if (!propertyId) return [];
      const { data, error } = await supabase
        .from("invoices")
        .select("*")
        .eq("property_id", propertyId)
        .order("created_at", { ascending: false });
      if (error) {
        if (error.code === "42P01") return [];
        throw error;
      }
      return (data ?? []) as InvoiceRow[];
    },
    enabled: !!propertyId,
  });
}

/** Single invoice with its line items (for invoice view). */
export function useInvoiceWithItems(invoiceId: string | undefined) {
  const invoiceQuery = useQuery({
    queryKey: ["invoice", invoiceId],
    queryFn: async () => {
      if (!invoiceId) return null;
      const { data, error } = await supabase
        .from("invoices")
        .select("*")
        .eq("id", invoiceId)
        .single();
      if (error) {
        if (error.code === "PGRST116" || error.code === "42P01") return null;
        throw error;
      }
      return data as InvoiceRow;
    },
    enabled: !!invoiceId,
  });

  const itemsQuery = useQuery({
    queryKey: ["invoice-items", invoiceId],
    queryFn: async () => {
      if (!invoiceId) return [];
      const { data, error } = await supabase
        .from("invoice_items")
        .select("*")
        .eq("invoice_id", invoiceId)
        .order("created_at", { ascending: true });
      if (error) {
        if (error.code === "42P01") return [];
        throw error;
      }
      return (data ?? []) as InvoiceItemRow[];
    },
    enabled: !!invoiceId,
  });

  return {
    invoice: invoiceQuery.data ?? null,
    items: itemsQuery.data ?? [],
    isLoading: invoiceQuery.isLoading || itemsQuery.isLoading,
    error: invoiceQuery.error || itemsQuery.error,
    refetch: () => {
      invoiceQuery.refetch();
      itemsQuery.refetch();
    },
  };
}

/** Generate invoice(s) for a period; returns created invoice ids. */
export function useCreateInvoiceFromPeriod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      propertyId,
      periodStart,
      periodEnd,
    }: {
      propertyId: string;
      periodStart: string; // YYYY-MM-DD
      periodEnd: string;
    }) => {
      const { data, error } = await supabase.rpc("create_invoice_from_period", {
        p_property_id: propertyId,
        p_period_start: periodStart,
        p_period_end: periodEnd,
      });
      if (error) throw error;
      return (data ?? []) as string[];
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["invoices-by-property", variables.propertyId] });
    },
  });
}

/** Mark invoice as paid. When property agreement payment_mode is on_app, call Stripe (charge + transfer to payee connected account) before this. */
export function useMarkInvoicePaid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const { error } = await supabase.rpc("mark_invoice_paid", { p_invoice_id: invoiceId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices-by-property"] });
      queryClient.invalidateQueries({ queryKey: ["invoice"] });
    },
  });
}
